import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff, Loader2, Copy, Check, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const SIDE_IMAGE = 'https://images.unsplash.com/photo-1736019510155-d2c9f60bb0d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwY2FyZ28lMjB2YW4lMjBoaWdod2F5JTIwcm9hZCUyMEFtZXJpY2FufGVufDB8fHx8MTc3NjMxMzIzM3ww&ixlib=rb-4.1.0&q=85';

function getRedirectPath(user) {
  if (!user) return '/login';
  if (user.is_admin) return '/admin';
  if (user.verification_status === 'approved') return '/profile';
  return '/verification';
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  // Forgot password state
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset password state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(true);
      if (data.reset_token) {
        const link = `${window.location.origin}/reset-password?token=${data.reset_token}`;
        setResetLink(link);
        toast.success('Reset link generated!');
      } else if (data.reset_link) {
        setResetLink(data.reset_link);
        toast.success('Reset link generated!');
      } else {
        toast.success('If that email exists, a reset link has been sent.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });

      if (data.access_token && data.user) {
        // Auto-login: store token and user
        sessionStorage.setItem('token', data.access_token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
        setResetDone(true);
        toast.success('Password reset! Signing you in...');
        const dest = getRedirectPath(data.user);
        setTimeout(() => {
          window.location.href = dest;
        }, 1500);
      } else {
        setResetDone(true);
        toast.success('Password reset successfully!');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to reset password.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(resetLink); }
    catch {
      const t = document.createElement('textarea');
      t.value = resetLink; t.style.position = 'fixed'; t.style.left = '-9999px';
      document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t);
    }
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // ===== RESET PASSWORD FORM (has token in URL) =====
  if (token) {
    if (resetDone) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Password Reset!</h2>
            <p className="text-slate-600">Signing you in...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="hidden lg:block lg:w-[40%] relative">
          <img src={SIDE_IMAGE} alt="Highway logistics" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
          <div className="absolute bottom-12 left-12 right-12 z-10">
            <div className="flex items-center gap-2.5 mb-6">
              <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
              <span className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
            </div>
            <h2 className="text-2xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Secure your account.</h2>
            <p className="text-slate-300 text-sm">Set a new password and get back to what matters.</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
          <div className="w-full max-w-[420px]">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="reset-back-login-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>

            <div className="lg:hidden flex items-center gap-2.5 mb-8">
              <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
            </div>

            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-100 mb-5">
              <Lock className="h-7 w-7 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Set new password</h2>
            <p className="text-slate-500 text-sm mb-8">Choose a strong password for your account</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3.5 mb-6 flex items-start gap-3" data-testid="reset-error-alert">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">New password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="Min 6 characters"
                    className="w-full h-12 px-4 pr-12 rounded-md border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                    autoComplete="new-password" data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1" data-testid="reset-toggle-pw">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">Confirm password</label>
                <input type={showPw ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  placeholder="Re-enter password"
                  className="w-full h-12 px-4 rounded-md border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                  autoComplete="new-password" data-testid="confirm-new-password-input" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="reset-password-btn">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Resetting...</> : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ===== RESET LINK GENERATED =====
  if (sent) {
    return (
      <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="hidden lg:block lg:w-[40%] relative">
          <img src={SIDE_IMAGE} alt="Highway logistics" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
          <div className="absolute bottom-12 left-12 right-12 z-10">
            <div className="flex items-center gap-2.5 mb-6">
              <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
              <span className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
            </div>
            <h2 className="text-2xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Almost there.</h2>
            <p className="text-slate-300 text-sm">Use the link to set a new password for your account.</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
          <div className="w-full max-w-[420px] text-center">
            <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-5" />
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {resetLink ? 'Reset Link Ready' : 'Check Your Email'}
            </h2>

            {resetLink ? (
              <>
                <p className="text-slate-500 text-sm mb-6">Click the button below to set your new password.</p>
                <a href={resetLink}
                  className="block w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-all flex items-center justify-center gap-2"
                  data-testid="reset-link-button">
                  Reset My Password
                </a>
                <button onClick={copyLink}
                  className="flex items-center justify-center gap-2 mx-auto mt-4 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  data-testid="copy-reset-link-btn">
                  {copied ? <><Check className="h-4 w-4 text-green-500" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy reset link</>}
                </button>
              </>
            ) : (
              <p className="text-slate-500 text-sm mb-6">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
            )}

            <div className="mt-8 pt-6 border-t border-slate-100">
              <Link to="/login" className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline underline-offset-4 transition-colors" data-testid="back-to-login-link">
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== FORGOT PASSWORD FORM =====
  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="hidden lg:block lg:w-[40%] relative">
        <img src={SIDE_IMAGE} alt="Highway logistics" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="flex items-center gap-2.5 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>
          <h2 className="text-2xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>The network that moves with you.</h2>
          <p className="text-slate-300 text-sm">Verified carriers. Real opportunities. One platform built for non-CDL logistics.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
        <div className="w-full max-w-[420px]">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="forgot-back-login-link">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Forgot password?</h2>
          <p className="text-slate-500 text-sm mb-8">Enter your email and we'll generate a reset link</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3.5 mb-6 flex items-start gap-3" data-testid="forgot-error-alert">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleForgotPassword} className="space-y-5" data-testid="forgot-password-form">
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-900 mb-2">Email address</label>
              <input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@company.com" autoComplete="email"
                className="w-full h-12 px-4 rounded-md border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                data-testid="forgot-email-input" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="send-reset-link-btn">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : 'Send Reset Link'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            Remember your password?{' '}
            <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline underline-offset-4 transition-colors">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
